<template>
  <div class="header__session">
    <div class="header__session--default">
      <FluidContainer ref="fluidContainer">
        <div class="header__session--wrapper">
          <p>{{ data.text }}</p>
          <div class="header__session--timer">
            <Icon icon="clock" />
            <span class="time">{{ time }}</span>
          </div>
        </div>
      </FluidContainer>
    </div>
    <div class="header__session--progress" :style="{ width: `${progress}%` }">
      <FluidContainer :style="{ paddingLeft: `${progressBarPadding}px` }">
        <div class="header__session--wrapper">
          <p>{{ data.text }}</p>
          <div class="header__session--timer">
            <Icon icon="clock" />
            <span class="time">{{ time }}</span>
          </div>
        </div>
      </FluidContainer>
    </div>

    <SessionModal v-if="warningModalOpen && shouldOpenModal" icon="session-ending"
      @click="extendSession" @close="closeWarningModal"
      :heading="data.warningHeader" :body="data.warningMessage" :buttonText="data.warningButtonText" />
      
    <SessionModal v-if="sessionEndModalOpen" icon="session-time-out"
      @click="offerRedirect" @close="closeSessionEndModal"
      :heading="data.timeoutHeader" :body="data.timeoutMessage" :buttonText="data.timeoutButtonText"
      class="session-end" />
  </div>
</template>

<script>
import moment from 'moment'
import Vue from 'vue'
import Component from 'vue-class-component'
import { mapActions } from 'vuex'

import SessionModal from '@/components/booking/SessionModal'
import FluidContainer from '@/components/FluidContainer'
import Icon from '@/components/Icon'
import Store from '@/store/index'
import { SET_SESSION_EXPIRED } from '@/store/mutation-types'
import MediaDetector from '@/utils/MediaDetector'

const DEFAULT_CONTAINER_PADDING = 4

@Component({
  store: Store,
  mixins: [
    MediaDetector,
  ],
  components: {
    FluidContainer,
    Icon,
    SessionModal,
  },
  props: {
    data: {
      type: Object,
      required: false,
    },
    backOfferForm: {
      type: HTMLFormElement,
      required: false,
    },
  },
  computed: {
    time () {
      return moment.utc(this.sessionTimeout * 1000).format('mm:ss')
    },
    progress () {
      return ((this.defaultTimeoutValue - this.sessionTimeout) / this.defaultTimeoutValue) * 100
    },
  },
  methods: {
    ...mapActions({
      setSessionExpired: `Booking/${SET_SESSION_EXPIRED}`,
    }),
  },
})
export default class BookingSessionBar extends Vue { 
  defaultTimeoutValue = this.data.sessionTimeout
  sessionTimeout = this.defaultTimeoutValue
  sessionTimeToExtend = this.data.sessionExtendedTime

  progressBarPadding = 0

  warningModalOpen = false
  sessionEndModalOpen = false
  shouldOpenModal = true

  timeCountdown () {
    const t = setInterval(() => {
      this.sessionTimeout = this.sessionTimeout - 1

      if (this.sessionTimeout === this.data.sessionWarningTime) this.openWarningModal()
      if (this.sessionTimeout === this.data.sessionWarningTime && this.shouldOpenModal) this.setSessionExpired(true)

      if (this.sessionTimeout === 0) {
        this.openSessionEndModal()
        clearInterval(t)
      }
    }, 1000)
  }

  updateProgressBarPadding () {    
    const fluidContainerWidth = this.$refs.fluidContainer.$el.offsetWidth
    if (this.isFluidDesktop) this.progressBarPadding = (window.innerWidth - fluidContainerWidth) / 2 + DEFAULT_CONTAINER_PADDING
    else this.progressBarPadding = 0
  }

  mounted () {
    window.addEventListener('resize', this.updateProgressBarPadding)

    this.updateProgressBarPadding()
    this.timeCountdown()
  }

  destroyed () {
    window.removeEventListener('resize', this.updateProgressBarPadding)
  }

  extendSession () {
    this.defaultTimeoutValue += this.sessionTimeToExtend
    this.sessionTimeout += this.sessionTimeToExtend
    
    this.preventModalOpening()    
    this.closeWarningModal()
    this.setSessionExpired(false)
  }

  preventModalOpening () {
    this.shouldOpenModal = false
  }

  openWarningModal () {
    this.warningModalOpen = true
  }

  closeWarningModal () {
    this.warningModalOpen = false
  }

  openSessionEndModal () {
    this.sessionEndModalOpen = true
    this.closeWarningModal()
    this.setSessionExpired(true)
  }

  closeSessionEndModal () {
    if (this.backOfferForm) this.backOfferForm.submit()
    else this.sessionEndModalOpen = false
  }

  offerRedirect () {
    if (this.backOfferForm) this.backOfferForm.submit()
    else log.warn('Redirect component or its WFFM form is not configured at this page ')
  }
}
</script>

<style lang="scss" scoped>
.header__session {
  display: flex;
  position: relative;
  height: 36px;
  align-items: center;
  font-size: 1em;
  font-family: 'Gotham Book';
  background-color: #fff;
  z-index: 111;

  @include mediaFluid(desktop) {
    position: sticky;
    top: 0;
  }

  .fluid-container {
    height: 100%;
  }

  &--wrapper {
    padding: 0 20px;
    display: flex;
    width: 100%;
    height: 100%;
    align-items: center;

    @include mediaFluid(tablet) {
      padding: 0;
    }
  }

  &--default {
    background-color: rgba(231, 31, 108, .2);
    color: #E71F6C;
    text-align: left;
    width: 100%;
    height: 100%;
  }

  &--progress {
    background-color: #E71F6C;
    color: #fff;
    width: 0;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    overflow: hidden;

    @include mediaFluid(desktop) {
      @media screen and (-webkit-min-device-pixel-ratio:0) {
        left: -2px;
      }
    }

    .fluid-container {
      max-width: none;
    }

    p {
      flex: 1 0 auto;
    }

    .header__session--timer {
      flex: 100%;
    }

    .header__session--wrapper {
      @include mediaFluid(tablet) {
        padding-left: 20px;
      }

      @include mediaFluid(desktop) {
        padding-left: 0;
      }
    }
  }

  &--timer {
    display: flex;
    align-items: center;
    font-weight: bold;
    font-size: 1.25em;
    font-family: 'Gotham';
    line-height: 1;

    .figure {
      margin-right: 8px;
      font-size: 26px;
    }

    .time {
      margin-top: 2px;
    }
  }

  p {
    margin: 2px 14px 0 0;

    @include mediaFluid(tablet) {
      margin-right: 9px;
    }
  }
}
</style>

<style lang="scss">
.session-end .session__modal--footer {
  background-color: #E71F6C !important;
}

.header__session .modal-dialog__wrapper {
  padding: 0;

  .modal-header {
    padding-right: 20px;

    @include mediaFluid(desktop) {
      padding-right: 0;
    }
  }
}
</style>


// WEBPACK FOOTER //
// src/components/BookingSessionBar.vue